import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { MongodbAtlasIconUrl } from "./icon.ts";
import { mongodbAtlasMcpTools } from "./mcp-tools.ts";
import type { MongodbAtlasAiIntegration } from "./types.ts";

// Measured on a test project: a new token worked on the MCP server at once.
const mongodbAtlasPropagationHint = propagationHint({
  resource: "MongoDB Atlas",
  normalWait: "1 to 5 seconds",
  maxWait: "30 seconds",
  pollInterval: "5 seconds",
});

const prefixArgs = ["mongodb-atlas-ai", "permission"];

const accessPrompt = `Request access to a MongoDB Atlas project by calling with argv:

  [<project>, <role>..., "--reason", <reason>]

where:

  - project: The Atlas project, as installed in P0
  - role: One or more Atlas project roles (e.g. 'GROUP_DATA_ACCESS_READ_ONLY'
    to read data, 'GROUP_DATA_ACCESS_READ_WRITE' to also write it). Request
    the least role that the tools you intend to call need. Use the list tool
    to enumerate them.
  - reason: The reason access is needed

Every tool is listed whether or not your access covers it; Atlas refuses a
call that your roles do not allow.

To work with data, call remote-atlas-connect with the Atlas project ID and the
cluster name first, then pass the connectionId it returns to the data tools.
atlas-list-projects and atlas-list-clusters find the IDs and names.
`;

export const mongodbAtlasAiMcp: McpServerSpec & { type: "external" } = {
  type: "external",
  label: "MongoDB Atlas",
  iconUrl: MongodbAtlasIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  tools: { type: "manual", tools: mongodbAtlasMcpTools },
  // The MCP configuration's project roles decide what Atlas allows.
  authorization: "upstream",
  // The hosted server answers at the root; "/mcp" is a 404.
  url: "https://mcp.mongodb.com/",
  access: {
    prompt: accessPrompt,
    prefixArgs,
    propagationHint: mongodbAtlasPropagationHint,
  },
  list: {
    fields: {
      project: {
        description: "MongoDB Atlas project, as installed in P0",
        required: [],
      },
      policies: {
        description: "Atlas project role that a grant can hold",
        required: ["project"],
      },
    },
    prefixArgs,
  },
};

/** Whether {@link mongodbAtlasAiMcp} can be added: only once an Atlas project
 * is installed, since without one there is nothing an agent could request. */
export const mongodbAtlasAiMcpServerAvailability = {
  mongodbAtlas: (installed: Record<string, NewConfig>): boolean =>
    installedIds(
      "credential",
      (installed["mongodb-atlas-ai"] ?? {}) as MongodbAtlasAiIntegration
    ).length > 0,
};
